import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { tagService } from '../utils/api';
import { toast } from 'react-hot-toast';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';

const TagQueuePage = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedDish, setSelectedDish] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSelectionsCache, setTagSelectionsCache] = useState({});
  const [initError, setInitError] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [previousLanguage, setPreviousLanguage] = useState(i18n.language);
  const searchTimeoutRef = useRef(null);
  const currentSchoolId = useSelector((state) => state.language?.currentSchoolId || null);
  
  const selectedLanguages = useSelector(state => state.language.selectedLanguages || []);
  const defaultLanguageCode = useSelector(state => state.language.defaultLanguageCode || 'en');
  
  // Get current UI language
  const uiLanguage = i18n.language || 'en';

  // Get the schoolId first, before it's used in any effects
  const getSchoolId = useCallback(() => {
    if (currentSchoolId) return currentSchoolId;
    if (userDetails?.current_school?.id) return userDetails.current_school.id;
    return null;
  }, [currentSchoolId, userDetails]);
  
  const schoolId = getSchoolId();

  // Track language changes to detect when we're switching languages
  useEffect(() => {
    if (previousLanguage !== i18n.language) {
      setPreviousLanguage(i18n.language);
    }
  }, [i18n.language, previousLanguage]);

  // Debounce search term changes to avoid excessive re-renders
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce time
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Effect to refresh tag data when language changes
  // We're no longer invalidating dishesNeedingTags here
  useEffect(() => {
    if (schoolId) {
      queryClient.invalidateQueries({ queryKey: ['tags', schoolId, uiLanguage] });
      queryClient.invalidateQueries({ queryKey: ['dishesNeedingTags', schoolId, uiLanguage] });
    }
  }, [uiLanguage, schoolId, queryClient]);

  // Function to properly construct the tag logo URL
  const getTagLogoUrl = useCallback((logoPath) => {
    if (!logoPath) return '';
    
    // If the path already has a protocol, return it as is
    if (logoPath.startsWith('http')) return logoPath;
    
    // Handle UUID format (Nhost storage)
    if (logoPath.includes('-') && !logoPath.includes('/')) {
      return `https://${process.env.REACT_APP_NHOST_SUBDOMAIN || 'gtwosmwmvopoqrodakmd'}.storage.${process.env.REACT_APP_NHOST_REGION || 'eu-central-1'}.nhost.run/v1/files/${logoPath}`;
    }
    
    // For relative paths, prepend API base URL
    if (logoPath.startsWith('/')) {
      return `${window.location.origin}${logoPath}`;
    }
    
    // For legacy path format
    return `${window.location.origin}/api/uploads/logos/${logoPath.split('/').pop()}`;
  }, []);

  // Fetch all tags for the school
  const { 
    data: tags = [], 
    isLoading: isLoadingTags, 
    isError: isTagsError 
  } = useQuery({
    queryKey: ['tags', schoolId, uiLanguage],
    queryFn: () => tagService.getTags(schoolId, uiLanguage),
    enabled: !!schoolId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch dishes needing tag information
  // uiLanguage is now included in the key to refetch when language changes
  const { 
    data: dishes = [],
    isLoading: loadingDishes,
    isError: isDishesError, 
    error: dishesFetchError,
    refetch: refetchDishes
  } = useQuery({
    queryKey: ['dishesNeedingTags', schoolId, uiLanguage],
    queryFn: async () => {
      const data = await tagService.getDishesNeedingTags(schoolId, uiLanguage);
      return data || [];
    },
    enabled: !!schoolId,
    staleTime: 1 * 60 * 1000, 
  });
  
  // Improve loading state logic to handle language switching
  const isLanguageSwitching = previousLanguage !== i18n.language;
  const loading = (loadingDishes || (!!schoolId && isLoadingTags)) && (!dishes.length || isLanguageSwitching);

  // Optional additional loading state for partial refreshes
  const isRefetching = (loadingDishes || isLoadingTags) && dishes.length > 0 && !isLanguageSwitching;

  // Effect to load user data
  useEffect(() => {
    try {
      setInitError(null);
      const userData = localStorage.getItem('user');
      if (userData) {
        setUserDetails(JSON.parse(userData));
      } else {
        setInitError('You must be logged in to view this page');
      }
    } catch (err) {
      setInitError('Error loading user data: ' + err.message);
    }
  }, []);

  // Effect to select the first dish when the queue loads/changes
  useEffect(() => {
    if (!loading && !isDishesError && dishes.length > 0) {
      // Check if we have no selected dish, or if the selected dish is no longer in the queue
      const selectedDishStillInQueue = selectedDish && dishes.some(dish => dish.id === selectedDish.id);
      
      if (!selectedDish || !selectedDishStillInQueue) {
        handleSelectDish(dishes[0]);
      }
    } else if ((!loading && dishes.length === 0 && selectedDish) || isDishesError) {
      setSelectedDish(null);
      setSelectedTags([]);
    }
  }, [dishes, loading, isDishesError, selectedDish]);

  // Effect to update the selected dish when dishes are refreshed (e.g., on language change)
  useEffect(() => {
    // Only run if we have dishes and a currently selected dish
    if (dishes.length > 0 && selectedDish) {
      // Find the updated version of the selected dish
      const updatedDish = dishes.find(dish => dish.id === selectedDish.id);
      
      // If found, update the selected dish to use the refreshed data
      if (updatedDish) {
        setSelectedDish(updatedDish);
      }
    }
  }, [dishes, selectedDish]);

  // Mutation for updating dish tags
  const updateDishTagsMutation = useMutation({
    mutationFn: ({ dishId, tagIds, noTags }) => 
      tagService.updateDishTags(dishId, tagIds, noTags),
    onSuccess: (data, variables) => {
      toast.success(t('tags.saved', 'Tags saved successfully'));
      
      // Invalidate the main list to refresh it with updated data (e.g., remove the dish if complete)
      queryClient.invalidateQueries({ queryKey: ['dishesNeedingTags', schoolId] });
      
      // Select the next dish logic (remains similar, but ensure `dishes` array is up-to-date from the invalidation)
      const currentDishIndex = dishes.findIndex(d => d.id === variables.dishId);
      const remainingDishes = dishes.filter(d => d.id !== variables.dishId);

      if (remainingDishes.length > 0) {
        const nextIndex = currentDishIndex >= remainingDishes.length ? 0 : currentDishIndex % remainingDishes.length;
        handleSelectDish(remainingDishes[nextIndex]);
      } else {
        setSelectedDish(null);
      }
    },
    onError: (error) => {
      toast.error(t('tags.saveError', 'Failed to save tags'));
      console.error('Error saving dish tags:', error);
    }
  });

  // Updated handleSelectDish to maintain tag selections cache
  const handleSelectDish = useCallback((dish) => {
    // Before switching dishes, save current selections to cache if we have a selected dish
    if (selectedDish) {
      setTagSelectionsCache(prev => ({
        ...prev,
        [selectedDish.id]: selectedTags
      }));
    }

    setSelectedDish(dish);
    
    // If we have cached selections for this dish, use them
    if (dish && tagSelectionsCache[dish.id]) {
      setSelectedTags(tagSelectionsCache[dish.id]);
    } else if (dish) {
      // Otherwise, use the dish's assigned_tag_ids from the API response
      if (dish.no_tags) {
        setSelectedTags([]);
      } else {
        setSelectedTags(dish.assigned_tag_ids || []);
      }
    } else {
      setSelectedTags([]);
    }
  }, [selectedDish, selectedTags, tagSelectionsCache]);

  // Handle search input change - only update local state, not query
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle tag checkbox change
  const handleTagChange = (tagId) => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  // Update handleConfirmNoTags to clear cached selections for the dish
  const handleConfirmNoTags = () => {
    if (!selectedDish) return;
    
    // Clear the cached selections for this dish when confirming no tags
    setTagSelectionsCache(prev => {
      const newCache = { ...prev };
      delete newCache[selectedDish.id];
      return newCache;
    });
    
    updateDishTagsMutation.mutate({
      dishId: selectedDish.id,
      tagIds: [],
      noTags: true
    });
  };

  // Update handleSaveSelectedTags to clear cached selections for the dish
  const handleSaveSelectedTags = () => {
    if (!selectedDish) return;
    if (selectedTags.length === 0) {
      toast.error(t('tags.selectAtLeastOneOrConfirmNone', 'Please select at least one tag, or use the \'Confirm: No Tags\' button.'));
      return;
    }
    
    // Clear the cached selections for this dish when saving
    setTagSelectionsCache(prev => {
      const newCache = { ...prev };
      delete newCache[selectedDish.id];
      return newCache;
    });
    
    updateDishTagsMutation.mutate({
      dishId: selectedDish.id,
      tagIds: selectedTags,
      noTags: false
    });
  };

  // Helper function to normalize translations from different response formats
  // Simplified to focus on the most common formats without excessive logging
  const getTranslations = useCallback((dish) => {
    if (!dish) return {};
    
    // Handle allTranslations object format (preferred)
    if (dish.allTranslations && typeof dish.allTranslations === 'object') {
      return dish.allTranslations;
    }
    
    // Handle translations array format from backend
    if (Array.isArray(dish.translations)) {
      return dish.translations.reduce((acc, trans) => {
        if (trans.language_code && trans.name) {
          acc[trans.language_code] = trans.name;
        }
        return acc;
      }, {});
    }
    
    // Handle name + language_code direct properties as fallback
    if (dish.name && dish.language_code) {
      const result = {};
      result[dish.language_code] = dish.name;
      return result;
    }

    return {};
  }, []);

  // Helper function to get dish name with language fallback
  const getDishName = useCallback((dish) => {
    if (!dish) return '';
    
    // If dish has a name property directly, use it first (for the current UI language)
    if (dish.name) return dish.name;
    
    // Get normalized translations
    const translations = getTranslations(dish);
    
    const uiLang = i18n.language;
    const schoolDefaultLang = defaultLanguageCode;
    const schoolSelectedLangsSet = new Set((selectedLanguages || []).map(l => l.code));
    const isSelectedLang = (code) => schoolSelectedLangsSet.has(code);

    // Try UI language first
    if (isSelectedLang(uiLang) && translations[uiLang]) return translations[uiLang];
    
    // Then try school default language
    if (isSelectedLang(schoolDefaultLang) && translations[schoolDefaultLang]) return translations[schoolDefaultLang];
    
    // Then try any other selected language
    const otherSelectedLangs = Array.from(schoolSelectedLangsSet)
      .filter(code => code !== uiLang && code !== schoolDefaultLang)
      .sort();
      
    for (const code of otherSelectedLangs) {
      if (translations[code]) return translations[code];
    }
    
    // Try any other available translation as last resort
    const firstAvailable = Object.entries(translations).sort(([langA], [langB]) => langA.localeCompare(langB)).find(([_, name]) => !!name);
    if (firstAvailable) return firstAvailable[1];
    
    // If no translations found at all
    return `Dish #${dish.id}`;
  }, [i18n.language, defaultLanguageCode, selectedLanguages, getTranslations]);

  // Filter dishes based on search term - use debounced term to prevent excessive re-renders
  const filteredDishes = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return dishes;
    
    const searchTermLower = debouncedSearchTerm.toLowerCase();
    return dishes.filter(dish => {
      const dishName = getDishName(dish).toLowerCase();
      return dishName.includes(searchTermLower);
    });
  }, [dishes, debouncedSearchTerm, getDishName]);

  // Sort tags by name for display
  const sortedTags = useMemo(() => {
    return [...tags].sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [tags]);

  // Add a visual indicator in the dish list to show dishes with unsaved changes
  const hasPendingChanges = useCallback((dish) => {
    if (!dish || !tagSelectionsCache[dish.id]) return false;
    
    // If the dish is in our cache, it means we have unsaved changes
    return true;
  }, [tagSelectionsCache]);

  if (initError) {
    return (
      <>
        <Navbar isAdmin={true} />
        <div className="max-w-7xl mx-auto p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {initError}
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navbar isAdmin={true} />
        <div className="max-w-7xl mx-auto p-4">
          <h1 className="text-2xl font-bold mb-6">
            {t('tags.queueTitle', 'Tag Queue')}
          </h1>
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <LoadingSpinner size="large" />
              <p className="mt-4 text-gray-600">
                {isLanguageSwitching 
                  ? t('common.loadingTranslations', 'Loading translations...')
                  : t('common.loading', 'Loading...')}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isDishesError || isTagsError) {
    return (
      <>
        <Navbar isAdmin={true} />
        <div className="max-w-7xl mx-auto p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {t('tags.loadError', 'Failed to load tag data')}
          </div>
          <button 
            onClick={refetchDishes}
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          >
            {t('common.retry', 'Retry')}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar isAdmin={true} />
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">
          {t('tags.queueTitle', 'Tag Queue')}
        </h1>
        
        {/* Overlay loading spinner for language switching */}
        {isRefetching && (
          <div className="fixed inset-0 bg-black bg-opacity-20 z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <LoadingSpinner size="large" />
              <p className="mt-4 text-center text-gray-600">{t('common.loading', 'Loading...')}</p>
            </div>
          </div>
        )}
        
        {dishes.length === 0 ? (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            {t('tags.allDishesComplete', 'All dishes have tag information. Great job!')}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left column: Dish list */}
            <div className="w-full md:w-1/3">
              <div className="bg-white p-4 rounded-lg shadow mb-4">
                <h2 className="text-lg font-semibold mb-3">
                  {t('tags.dishes', 'Dishes')} ({filteredDishes.length})
                </h2>
                
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder={t('common.search', 'Search...')}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                
                <div className="max-h-[60vh] overflow-y-auto">
                  {filteredDishes.length === 0 ? (
                    <div className="text-gray-500 py-3 text-center">
                      {searchTerm 
                        ? t('tags.noSearchResults', 'No dishes match your search')
                        : t('tags.noDishesNeedTags', 'No dishes need tag information')}
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {filteredDishes.map(dish => {
                        // For each dish, get its name in the current language
                        const dishName = getDishName(dish);
                        const hasUnsavedChanges = hasPendingChanges(dish);
                        
                        return (
                          <li 
                            key={dish.id}
                            className={`py-2 px-2 cursor-pointer hover:bg-gray-100 ${
                              selectedDish?.id === dish.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                            } ${hasUnsavedChanges ? 'border-r-4 border-r-amber-400' : ''}`}
                            onClick={() => handleSelectDish(dish)}
                          >
                            <div className="font-medium flex items-center justify-between">
                              <div>
                                {dishName}
                              </div>
                              {hasUnsavedChanges && (
                                <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full" title={t('tags.unsavedChanges', 'Unsaved changes')}>
                                  {t('tags.pendingChanges', 'Pending')}
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right column: Tag selection */}
            <div className="w-full md:w-2/3">
              <div className="bg-white p-4 rounded-lg shadow">
                {selectedDish ? (
                  <>
                    <h2 className="text-lg font-semibold mb-3 flex items-center">
                      <span>{t('tags.assignFor', 'Assign tags for')}: {getDishName(selectedDish)}</span>
                      {hasPendingChanges(selectedDish) && (
                        <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                          {t('tags.unsavedChanges', 'Unsaved changes')}
                        </span>
                      )}
                    </h2>
                    
                    {/* "Confirm No Tags" Button - replaces checkbox */}
                    <div className="mb-4">
                      <button
                        onClick={handleConfirmNoTags}
                        disabled={updateDishTagsMutation.isPending || !selectedDish}
                        className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded flex items-center justify-center disabled:opacity-50"
                      >
                        {updateDishTagsMutation.isPending && updateDishTagsMutation.variables?.noTags === true ? (
                          <LoadingSpinner size="small" className="mr-2" />
                        ) : null}
                        {t('tags.confirmNoTags', 'Confirm: No Tags')}
                      </button>
                    </div>
                    
                    {/* Tag Selection Area */}
                    <div className="mb-6">
                      <h3 className="font-medium mb-3 text-gray-700">
                        {t('tags.selectTags', 'Select tags in this dish')}:
                      </h3>
                      
                      {sortedTags.length === 0 ? (
                        <div className="text-yellow-700 bg-yellow-50 p-3 rounded">
                          {t('tags.noTagsConfigured', 'No tags configured for this school yet. Please add tags in the Settings page first.')}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {sortedTags.map(tag => (
                            <button 
                              key={tag.id}
                              onClick={() => handleTagChange(tag.id)}
                              className={`flex items-center justify-center p-1 border rounded-lg text-xl transition-colors duration-150 ease-in-out
                                ${selectedTags.includes(tag.id)
                                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                }
                              `}
                            >
                              {tag.logo_path && (
                                <img 
                                  src={getTagLogoUrl(tag.logo_path)} 
                                  alt={tag.name}
                                  className="h-12 w-12 mr-2 object-contain flex-shrink-0"
                                />
                              )}
                              <span className="truncate">{tag.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* "Save Selected Tags" Button */}
                    <div className="flex justify-end mt-6">
                      <button
                        onClick={handleSaveSelectedTags}
                        disabled={updateDishTagsMutation.isPending || !selectedDish || selectedTags.length === 0}
                        className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded flex items-center justify-center disabled:opacity-50"
                      >
                        {updateDishTagsMutation.isPending && updateDishTagsMutation.variables?.noTags === false ? (
                           <LoadingSpinner size="small" className="mr-2" />
                        ) : null}
                        {updateDishTagsMutation.isPending && updateDishTagsMutation.variables?.noTags === false
                          ? t('common.saving', 'Saving...')
                          : t('common.saveSelectedTags', 'Save Selected Tags')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {dishes.length > 0
                      ? t('tags.selectDish', 'Select a dish to assign tags')
                      : t('tags.noDishesNeedTags', 'No dishes need tag information')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TagQueuePage; 